import { useEffect, useRef, useState } from 'react';
import { Badge, EmptyState, ErrorMessage, Field, Loading, Modal, SearchSelect, StatCard, Toggle, useAsync, useToast } from '../../components/ui';
import { IconCheck, IconEdit, IconFile, IconPlus, IconTrash, IconUpload } from '../../components/icons';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatCurrency, formatDate, formatNumber } from '../../lib/format';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Erfasst',
  booked: 'Gebucht',
  paid: 'Bezahlt',
  cancelled: 'Storniert',
};

const STATUS_VARIANTS: Record<string, string> = {
  draft: 'warning',
  booked: 'primary',
  paid: 'success',
  cancelled: '',
};

/**
 * Eingangsrechnungen (Kap. 7).
 *
 * Erfassen und Buchen sind bewusst getrennt: Eine Rechnung darf geprüft und
 * korrigiert werden, ohne dass jeder Zwischenstand schon Lagerbestand
 * erzeugt. Erst das Buchen schreibt Zugänge fort – und zwar je Position auf
 * die Lager, auf die sie verteilt wurde.
 */
export function InvoicesPanel({ warehouses }: { warehouses: any[] }) {
  const { can } = useAuth();
  const { notify } = useToast();
  const [editing, setEditing] = useState<any | null>(null);
  const [booking, setBooking] = useState<any | null>(null);

  const invoices = useAsync(() => api.get('/api/stocktaking/invoices'), []);
  const suppliers = useAsync(() => api.get('/api/procurement/suppliers').catch(() => []), []);
  const articles = useAsync(() => api.get('/api/material/articles').catch(() => []), []);

  const mayManage = can('material.invoices');
  const list: any[] = invoices.data ?? [];

  const open = list.filter((entry) => entry.status === 'draft');
  const unpaid = list.filter((entry) => entry.status === 'booked');

  const pay = async (invoice: any) => {
    await api.post(`/api/stocktaking/invoices/${invoice.id}/pay`);
    notify('Rechnung als bezahlt vermerkt.', 'success');
    invoices.reload();
  };

  const remove = async (invoice: any) => {
    const isDraft = invoice.status === 'draft';
    if (
      !window.confirm(
        isDraft
          ? `Rechnung ${invoice.invoice_no} löschen?`
          : `Rechnung ${invoice.invoice_no} stornieren? Die gebuchten Zugänge bleiben bestehen.`,
      )
    ) {
      return;
    }
    await api.delete(`/api/stocktaking/invoices/${invoice.id}`);
    notify(isDraft ? 'Rechnung gelöscht.' : 'Rechnung storniert.', 'success');
    invoices.reload();
  };

  if (invoices.loading) return <Loading />;

  return (
    <div className="stack">
      <div className="grid cols-4">
        <StatCard
          label="Zu buchen"
          value={open.length}
          tone={open.length > 0 ? 'warning' : 'success'}
          icon={<IconFile width={17} height={17} />}
          hint="erfasst, noch ohne Bestandszugang"
        />
        <StatCard
          label="Offen zu zahlen"
          value={formatCurrency(unpaid.reduce((sum, entry) => sum + (entry.gross_amount ?? 0), 0))}
          tone="primary"
          icon={<IconFile width={17} height={17} />}
          hint={`${unpaid.length} gebuchte Rechnung(en)`}
        />
        <StatCard
          label="Rechnungen gesamt"
          value={list.length}
          tone="info"
          icon={<IconFile width={17} height={17} />}
        />
        <StatCard
          label="Volumen"
          value={formatCurrency(
            list.filter((entry) => entry.status !== 'cancelled').reduce((sum, entry) => sum + (entry.gross_amount ?? 0), 0),
          )}
          tone="accent"
          icon={<IconFile width={17} height={17} />}
          hint="brutto, ohne Stornos"
        />
      </div>

      <div className="row between">
        <span className="section-title">Eingangsrechnungen</span>
        {mayManage && (
          <button className="btn sm primary" onClick={() => setEditing({ isNew: true })}>
            <IconPlus width={14} height={14} /> Rechnung erfassen
          </button>
        )}
      </div>

      {list.length === 0 ? (
        <EmptyState
          icon={<IconFile width={20} height={20} />}
          title="Keine Eingangsrechnung erfasst"
          hint="Lieferantenrechnungen lassen sich hier erfassen und mit Bestandszugang buchen."
        />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Beleg</th>
                <th>Lieferant</th>
                <th>Datum</th>
                <th>Fällig</th>
                <th className="num">Positionen</th>
                <th className="num">Netto</th>
                <th className="num">Brutto</th>
                <th>Status</th>
                {mayManage && <th className="actions">Aktionen</th>}
              </tr>
            </thead>
            <tbody>
              {list.map((invoice) => (
                <tr key={invoice.id}>
                  <td>
                    <strong className="mono">{invoice.invoice_no}</strong>
                    {invoice.order_no && <div className="small subtle">zu {invoice.order_no}</div>}
                  </td>
                  <td className="small">{invoice.supplier_name ?? '–'}</td>
                  <td className="small">{invoice.invoice_date ? formatDate(invoice.invoice_date) : '–'}</td>
                  <td className="small">{invoice.due_date ? formatDate(invoice.due_date) : '–'}</td>
                  <td className="num">{invoice.item_count}</td>
                  <td className="num">{formatCurrency(invoice.net_amount)}</td>
                  <td className="num">
                    <strong>{formatCurrency(invoice.gross_amount)}</strong>
                  </td>
                  <td>
                    <Badge variant={STATUS_VARIANTS[invoice.status] ?? ''}>
                      {STATUS_LABELS[invoice.status] ?? invoice.status}
                    </Badge>
                  </td>
                  {mayManage && (
                    <td className="actions">
                      {invoice.status === 'draft' && (
                        <>
                          <button className="btn sm" onClick={() => setEditing(invoice)}>
                            <IconEdit width={13} height={13} />
                          </button>
                          <button className="btn sm primary" style={{ marginLeft: 6 }} onClick={() => setBooking(invoice)}>
                            <IconCheck width={13} height={13} /> Buchen
                          </button>
                        </>
                      )}
                      {invoice.status === 'booked' && (
                        <button className="btn sm" onClick={() => pay(invoice)}>
                          Bezahlt
                        </button>
                      )}
                      <button className="btn sm ghost" style={{ marginLeft: 6 }} onClick={() => remove(invoice)}>
                        <IconTrash width={13} height={13} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <InvoiceModal
          invoice={editing}
          suppliers={suppliers.data ?? []}
          articles={articles.data ?? []}
          warehouses={warehouses}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invoices.reload();
            notify('Rechnung gespeichert.', 'success');
          }}
        />
      )}

      {booking && (
        <BookingModal
          invoice={booking}
          warehouses={warehouses}
          onClose={() => setBooking(null)}
          onBooked={(movements) => {
            setBooking(null);
            invoices.reload();
            notify(`Rechnung gebucht – ${movements} Zugang/Zugänge erzeugt.`, 'success');
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Erfassung                                                           */
/* ------------------------------------------------------------------ */

function InvoiceModal({
  invoice,
  suppliers,
  articles,
  warehouses,
  onClose,
  onSaved,
}: {
  invoice: any;
  suppliers: any[];
  articles: any[];
  warehouses: any[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = Boolean(invoice.isNew);
  const [head, setHead] = useState({
    supplierId: '',
    invoiceNo: '',
    invoiceDate: new Date().toISOString().slice(0, 10),
    dueDate: '',
    note: '',
  });
  const [items, setItems] = useState<any[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const [useAi, setUseAi] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [extractResult, setExtractResult] = useState<{ source: 'ai' | 'heuristic' } | null>(null);
  const [extractError, setExtractError] = useState<unknown>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const availability = useAsync(
    () => (isNew ? api.get('/api/stocktaking/invoices/extract-availability') : Promise.resolve(null)),
    [isNew],
  );

  const detail = useAsync(
    () => (isNew ? Promise.resolve(null) : api.get(`/api/stocktaking/invoices/${invoice.id}`)),
    [invoice.id],
  );

  const extractFromPdf = async (file: File) => {
    setExtracting(true);
    setExtractError(null);
    setExtractResult(null);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error('Die Datei konnte nicht gelesen werden.'));
        reader.readAsDataURL(file);
      });
      const result: any = await api.post('/api/stocktaking/invoices/extract', { dataUrl, useAi });

      setHead((current) => ({
        ...current,
        supplierId: result.supplierId ? String(result.supplierId) : current.supplierId,
        invoiceNo: result.invoiceNo || current.invoiceNo,
        invoiceDate: result.invoiceDate || current.invoiceDate,
        dueDate: result.dueDate || current.dueDate,
      }));
      if ((result.items ?? []).length > 0) {
        setItems(
          result.items.map((item: any) => ({
            articleId: '',
            description: item.description || '',
            quantity: item.quantity || 1,
            unit: item.unit || 'Stk',
            unitPrice: item.unitPrice || 0,
            taxRate: item.taxRate || 19,
            allocations: [],
          })),
        );
      }
      setExtractResult({ source: result.source });
    } catch (caught) {
      setExtractError(caught);
    } finally {
      setExtracting(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  useEffect(() => {
    if (!detail.data) return;
    setHead({
      supplierId: detail.data.supplier_id ? String(detail.data.supplier_id) : '',
      invoiceNo: detail.data.invoice_no ?? '',
      invoiceDate: detail.data.invoice_date?.slice(0, 10) ?? '',
      dueDate: detail.data.due_date?.slice(0, 10) ?? '',
      note: detail.data.note ?? '',
    });
    setItems(
      (detail.data.items ?? []).map((item: any) => ({
        articleId: item.article_id ? String(item.article_id) : '',
        description: item.description,
        quantity: item.quantity,
        unit: item.unit ?? '',
        unitPrice: item.unit_price,
        taxRate: item.tax_rate,
        allocations: (item.allocations ?? []).map((entry: any) => ({
          warehouseId: String(entry.warehouse_id),
          quantity: entry.quantity,
        })),
      })),
    );
  }, [detail.data]);

  const addItem = () =>
    setItems((current) => [
      ...current,
      { articleId: '', description: '', quantity: 1, unit: 'Stk', unitPrice: 0, taxRate: 19, allocations: [] },
    ]);

  const setItem = (index: number, patch: Record<string, unknown>) =>
    setItems((current) => current.map((item, position) => (position === index ? { ...item, ...patch } : item)));

  /** Wird ein Artikel gewählt, übernimmt die Position dessen Stammdaten. */
  const pickArticle = (index: number, articleId: string) => {
    const article = articles.find((entry: any) => String(entry.id) === articleId);
    setItem(index, {
      articleId,
      description: article ? article.name : items[index].description,
      unit: article ? article.unit : items[index].unit,
      unitPrice: article ? article.purchase_price : items[index].unitPrice,
    });
  };

  const net = items.reduce((sum, item) => sum + Number(item.quantity || 0) * Number(item.unitPrice || 0), 0);
  const tax = items.reduce(
    (sum, item) => sum + Number(item.quantity || 0) * Number(item.unitPrice || 0) * (Number(item.taxRate || 0) / 100),
    0,
  );

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = {
        supplierId: head.supplierId ? Number(head.supplierId) : null,
        invoiceNo: head.invoiceNo,
        invoiceDate: head.invoiceDate || null,
        dueDate: head.dueDate || null,
        note: head.note || null,
        items: items.map((item) => ({
          articleId: item.articleId ? Number(item.articleId) : null,
          description: item.description,
          quantity: Number(item.quantity) || 0,
          unit: item.unit || null,
          unitPrice: Number(item.unitPrice) || 0,
          taxRate: Number(item.taxRate) || 0,
          allocations: (item.allocations ?? [])
            .filter((entry: any) => entry.warehouseId && Number(entry.quantity) !== 0)
            .map((entry: any) => ({ warehouseId: Number(entry.warehouseId), quantity: Number(entry.quantity) })),
        })),
      };
      if (isNew) await api.post('/api/stocktaking/invoices', payload);
      else await api.patch(`/api/stocktaking/invoices/${invoice.id}`, payload);
      onSaved();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isNew ? 'Eingangsrechnung erfassen' : `Rechnung ${invoice.invoice_no}`}
      size="xwide"
      onClose={onClose}
      footer={
        <>
          <span className="small subtle" style={{ marginRight: 'auto' }}>
            Netto {formatCurrency(net)} · MwSt. {formatCurrency(tax)} ·{' '}
            <strong>Brutto {formatCurrency(net + tax)}</strong>
          </span>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn primary" onClick={save} disabled={busy || !head.invoiceNo || items.length === 0}>
            Speichern
          </button>
        </>
      }
    >
      {detail.loading ? (
        <Loading />
      ) : (
        <div className="stack">
          <ErrorMessage error={error} />

          {isNew && (
            <div className="stack" style={{ gap: 8 }}>
              <input
                ref={fileInput}
                type="file"
                accept="application/pdf"
                style={{ display: 'none' }}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void extractFromPdf(file);
                }}
              />
              <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
                <button className="btn sm" disabled={extracting} onClick={() => fileInput.current?.click()}>
                  <IconUpload width={14} height={14} /> {extracting ? 'Wird gelesen …' : 'PDF hochladen'}
                </button>
                {availability.data?.aiAvailable && (
                  <Toggle checked={useAi} onChange={setUseAi} label="KI-gestützte Extraktion verwenden" />
                )}
              </div>
              <ErrorMessage error={extractError} />
              {extractResult && (
                <div className="alert info" style={{ fontSize: 12.5 }}>
                  {extractResult.source === 'ai'
                    ? 'Felder aus der PDF-Datei per KI übernommen –'
                    : 'Felder aus der PDF-Datei übernommen –'}{' '}
                  bitte prüfen und bei Bedarf korrigieren.
                </div>
              )}
            </div>
          )}

          <div className="form-grid">
            <Field label="Belegnummer" required>
              <input
                autoFocus
                value={head.invoiceNo}
                onChange={(event) => setHead({ ...head, invoiceNo: event.target.value })}
              />
            </Field>
            <Field label="Lieferant">
              <SearchSelect
                value={String(head.supplierId ?? '')}
                onChange={(wert) => setHead({ ...head, supplierId: wert })}
                options={(suppliers ?? []).map((supplier: any) => ({ value: String(supplier.id), label: String(supplier.name) }))}
                placeholder="Ohne Zuordnung"
              />
            </Field>
            <Field label="Rechnungsdatum">
              <input
                type="date"
                value={head.invoiceDate}
                onChange={(event) => setHead({ ...head, invoiceDate: event.target.value })}
              />
            </Field>
            <Field label="Fällig am">
              <input type="date" value={head.dueDate} onChange={(event) => setHead({ ...head, dueDate: event.target.value })} />
            </Field>
          </div>

          <div className="row between">
            <span className="section-title">Positionen</span>
            <button className="btn sm" onClick={addItem}>
              <IconPlus width={13} height={13} /> Position
            </button>
          </div>

          {items.length === 0 && (
            <span className="small subtle">
              Noch keine Position. Positionen mit Artikel erzeugen beim Buchen einen Lagerzugang; Fracht oder
              Pauschalen ohne Artikel bleiben reine Belegzeilen.
            </span>
          )}

          {items.map((item, index) => (
            <InvoiceItemRow
              key={index}
              item={item}
              index={index}
              articles={articles}
              warehouses={warehouses}
              onChange={(patch) => setItem(index, patch)}
              onRemove={() => setItems((current) => current.filter((_, position) => position !== index))}
              onPickArticle={(value) => pickArticle(index, value)}
            />
          ))}

          <Field label="Notiz">
            <textarea value={head.note} onChange={(event) => setHead({ ...head, note: event.target.value })} />
          </Field>
        </div>
      )}
    </Modal>
  );
}

/** Eine Rechnungsposition samt Verteilung auf Lager. */
function InvoiceItemRow({
  item,
  index,
  articles,
  warehouses,
  onChange,
  onRemove,
  onPickArticle,
}: {
  item: any;
  index: number;
  articles: any[];
  warehouses: any[];
  onChange: (patch: Record<string, unknown>) => void;
  onRemove: () => void;
  onPickArticle: (value: string) => void;
}) {
  const allocated = (item.allocations ?? []).reduce((sum: number, entry: any) => sum + Number(entry.quantity || 0), 0);
  const quantity = Number(item.quantity) || 0;
  const rest = Number((quantity - allocated).toFixed(3));

  const addAllocation = () =>
    onChange({
      allocations: [
        ...(item.allocations ?? []),
        { warehouseId: warehouses.find((entry: any) => entry.is_default)?.id ?? warehouses[0]?.id ?? '', quantity: rest > 0 ? rest : 0 },
      ],
    });

  const setAllocation = (position: number, patch: Record<string, unknown>) =>
    onChange({
      allocations: (item.allocations ?? []).map((entry: any, index2: number) =>
        index2 === position ? { ...entry, ...patch } : entry,
      ),
    });

  return (
    <div className="record-form">
      <div className="row between" style={{ marginBottom: 8 }}>
        <span className="section-title">Position {index + 1}</span>
        <button className="btn sm ghost" onClick={onRemove} title="Position entfernen">
          <IconTrash width={13} height={13} />
        </button>
      </div>

      <div className="form-grid">
        <Field label="Artikel" hint="Leer lassen für Fracht oder Pauschalen.">
          <SearchSelect
            value={String(item.articleId ?? '')}
            onChange={(wert) => onPickArticle(wert)}
            options={(articles ?? []).map((article: any) => ({ value: String(article.id), label: `${article.article_no} · ${article.name}` }))}
            placeholder="Ohne Artikel"
          />
        </Field>
        <Field label="Bezeichnung" required>
          <input value={item.description} onChange={(event) => onChange({ description: event.target.value })} />
        </Field>
        <Field label="Menge">
          <input
            type="number"
            step="0.01"
            value={item.quantity}
            onChange={(event) => onChange({ quantity: event.target.value })}
          />
        </Field>
        <Field label="Einheit">
          <input value={item.unit ?? ''} onChange={(event) => onChange({ unit: event.target.value })} />
        </Field>
        <Field label="Einzelpreis (netto)">
          <input
            type="number"
            step="0.01"
            value={item.unitPrice}
            onChange={(event) => onChange({ unitPrice: event.target.value })}
          />
        </Field>
        <Field label="MwSt. %">
          <input
            type="number"
            step="0.1"
            value={item.taxRate}
            onChange={(event) => onChange({ taxRate: event.target.value })}
          />
        </Field>
      </div>

      {item.articleId && (
        <div className="stack" style={{ gap: 8, marginTop: 12 }}>
          <div className="row between">
            <span className="section-title">Verteilung auf Lager</span>
            <button className="btn sm" onClick={addAllocation}>
              <IconPlus width={13} height={13} /> Lager
            </button>
          </div>

          {(item.allocations ?? []).length === 0 && (
            <span className="small subtle">
              Ohne Verteilung geht die volle Menge beim Buchen in das dort gewählte Vorgabelager.
            </span>
          )}

          {(item.allocations ?? []).map((entry: any, position: number) => (
            <div key={position} className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
              <SearchSelect
                value={String(entry.warehouseId ?? '')}
                onChange={(wert) => setAllocation(position, { warehouseId: wert })}
                options={(warehouses ?? []).map((warehouse: any) => ({ value: String(warehouse.id), label: String(warehouse.name) }))}
                placeholder="Lager wählen …"
                style={{ flex: 1 }}
              />
              <input
                type="number"
                step="0.01"
                value={entry.quantity}
                onChange={(event) => setAllocation(position, { quantity: event.target.value })}
                style={{ width: 120 }}
              />
              <button
                className="btn sm ghost"
                onClick={() =>
                  onChange({
                    allocations: (item.allocations ?? []).filter((_: any, index2: number) => index2 !== position),
                  })
                }
              >
                <IconTrash width={13} height={13} />
              </button>
            </div>
          ))}

          {(item.allocations ?? []).length > 0 && (
            <span className={`small ${Math.abs(rest) < 0.001 ? 'subtle' : ''}`} style={rest !== 0 ? { color: 'var(--danger-text)' } : undefined}>
              {Math.abs(rest) < 0.001
                ? `Verteilt: ${formatNumber(allocated, 2)} von ${formatNumber(quantity, 2)} – vollständig.`
                : `Verteilt: ${formatNumber(allocated, 2)} von ${formatNumber(quantity, 2)} – ${rest > 0 ? `${formatNumber(rest, 2)} offen` : `${formatNumber(-rest, 2)} zu viel`}.`}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Buchen                                                              */
/* ------------------------------------------------------------------ */

function BookingModal({
  invoice,
  warehouses,
  onClose,
  onBooked,
}: {
  invoice: any;
  warehouses: any[];
  onClose: () => void;
  onBooked: (movements: number) => void;
}) {
  const [defaultWarehouseId, setDefaultWarehouseId] = useState<string>(
    String(warehouses.find((entry: any) => entry.is_default)?.id ?? warehouses[0]?.id ?? ''),
  );
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const detail = useAsync(() => api.get(`/api/stocktaking/invoices/${invoice.id}`), [invoice.id]);

  const book = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post(`/api/stocktaking/invoices/${invoice.id}/book`, {
        defaultWarehouseId: defaultWarehouseId ? Number(defaultWarehouseId) : null,
      });
      onBooked(result.movements);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const items: any[] = detail.data?.items ?? [];
  const stockItems = items.filter((item) => item.article_id);

  return (
    <Modal
      title={`Rechnung ${invoice.invoice_no} buchen`}
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn primary" onClick={book} disabled={busy}>
            <IconCheck width={14} height={14} /> Buchen und Bestand zuschreiben
          </button>
        </>
      }
    >
      {detail.loading ? (
        <Loading />
      ) : (
        <div className="stack">
          <ErrorMessage error={error} />

          <div className="note-box tone-info">
            <IconFile width={15} height={15} />
            <span>
              <strong>{stockItems.length} von {items.length} Positionen</strong> erzeugen einen Lagerzugang.
              Positionen ohne Artikel – Fracht, Pauschalen – gehören zum Beleg, nicht ins Lager.
            </span>
          </div>

          <Field
            label="Vorgabelager"
            hint="Gilt für alle Positionen ohne eigene Verteilung."
          >
            <SearchSelect
              value={String(defaultWarehouseId ?? '')}
              onChange={(wert) => setDefaultWarehouseId(wert)}
              options={(warehouses ?? []).map((warehouse: any) => ({ value: String(warehouse.id), label: `${warehouse.name} ${warehouse.is_default ? ' (Vorgabe)' : ''}` }))}
              placeholder="Kein Vorgabelager"
            />
          </Field>

          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Position</th>
                  <th className="num">Menge</th>
                  <th>Zugang nach</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.description}
                      {item.article_no && <div className="small subtle mono">{item.article_no}</div>}
                    </td>
                    <td className="num">
                      {formatNumber(item.quantity, 2)} {item.unit ?? ''}
                    </td>
                    <td className="small">
                      {!item.article_id ? (
                        <span className="subtle">kein Lagerzugang</span>
                      ) : item.allocations?.length ? (
                        item.allocations.map((entry: any) => (
                          <div key={entry.id}>
                            {formatNumber(entry.quantity, 2)} → {entry.warehouse_name}
                          </div>
                        ))
                      ) : (
                        <span>
                          {formatNumber(item.quantity, 2)} →{' '}
                          {warehouses.find((entry: any) => String(entry.id) === defaultWarehouseId)?.name ?? (
                            <span style={{ color: 'var(--danger-text)' }}>Vorgabelager fehlt</span>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}
